import React, { useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TarotContext } from '../context/TarotContext';
import { tarotCards } from '../data/tarotCards';
import '../styles/ReadingPage.css';
import LoadingIndicator from '../components/LoadingIndicator';
import { cardBackBase64 } from '../utils/cardBackImage';
import CardSelectionScreen from '../components/TarotCardSelection';
import ReadingResult from '../components/ReadingResult';
import axios from 'axios';
import SEO from '../components/SEO';

function ReadingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    question, 
    setQuestion, 
    reading, 
    getReading, 
    loading, 
    error,
    setError
  } = useContext(TarotContext);

  // Initialize step from location state or default to 2
  const [step, setStep] = useState(() => location.state?.step || 2);
  const [shuffledCards, setShuffledCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [finalReading, setFinalReading] = useState(null);
  const [finalCard, setFinalCard] = useState(null);
  const [finalQuestion, setFinalQuestion] = useState("");

  // Initialize cards on mount
  useEffect(() => {
    const shuffled = [...tarotCards].sort(() => Math.random() - 0.5).slice(0, 3);
    setShuffledCards(shuffled);
    
    if (location.state?.question) {
      setQuestion(location.state.question);
    }

    // Cleanup function to clear question when component unmounts
    return () => {
      setQuestion('');
    };
  }, [location.state?.question, setQuestion]);

  // Handle reading updates
  useEffect(() => {
    if (reading) {
      setFinalReading(reading);
      setFinalCard(selectedCard);
      setFinalQuestion(question);
      
      if (selectedCard) {
        setStep(3);
      }
    }
  }, [reading, selectedCard, question]);

  const handleCardSelect = async (card) => {
    if (loading) return;
    
    console.log('Selected card:', card);
    
    setSelectedCard(card);
    try {
      // Send the card ID number instead of name
      await getReading(question, String(card.id));
    } catch (error) {
      console.error('Detailed error:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });
      
      if (error.response?.status === 429) {
        setError('You have reached your daily reading limit. Please try again tomorrow.');
      } else {
        setError(`Failed to get reading: ${error.message}`);
      }
    }
  };

  const handleNewReading = () => {
    navigate('/', { replace: true });
  };

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  console.log('Is mobile device:', isMobile);

  // Error handling
  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={handleNewReading}>Try Again</button>
      </div>
    );
  }

  // Main render
  return (
    <>
      <SEO 
        title="Lectura de Tarot Personalizada | Interpretación Gratuita"
        description="Interpretación detallada de tu tirada de tarot. Descubre el significado de las cartas y recibe consejos personalizados."
        keywords="interpretación tarot, significado cartas, lectura personal, consulta tarot online"
      />
      <div className="reading-page">
        {loading ? (
          <LoadingIndicator />
        ) : (
          <>
            {step === 2 && (
              <CardSelectionScreen
                cards={shuffledCards}
                onCardSelect={handleCardSelect}
                loading={loading}
              />
            )}
            {step === 3 && finalReading && (
              <ReadingResult
                reading={finalReading}
                card={finalCard}
                question={finalQuestion}
                onNewReading={handleNewReading}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

export default ReadingPage; 